// ISO 27001:2022 Pro Edition - Complete 93 Controls Questions Data
// Organized by 4 Control Categories as per Annex A

export const PRO_QUESTION_TYPES = {
  RATING: 'rating',        // 5-star rating (1-5)
  YES_NO: 'yes_no',        // Yes/No/Unaware
  TRUE_FALSE: 'true_false' // True/False
};

// Scoring weights for different question types
export const PRO_SCORING = {
  rating: {
    1: 1, 2: 2, 3: 3, 4: 4, 5: 5,  // Direct score
    maxScore: 5
  },
  yes_no: {
    yes: 5, no: 1, unaware: 2,
    maxScore: 5
  },
  true_false: {
    true: 5, false: 1,
    maxScore: 5
  }
};

// Category weights for Pro Edition (based on control count)
export const PRO_CATEGORY_WEIGHTS = {
  organizational: 0.40,  // 37 controls - 40%
  people: 0.09,          // 8 controls - 9%
  physical: 0.15,        // 14 controls - 15%
  technological: 0.36    // 34 controls - 36%
};

// A.5 - ORGANIZATIONAL CONTROLS (37 Questions)
export const ORGANIZATIONAL_CONTROLS = [
  {
    id: 'A_5_1',
    controlId: 'A.5.1',
    controlName: 'Policies for information security',
    question: 'Has your organization defined, approved, and communicated information security policies that are reviewed at planned intervals?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Information Security Policy'
  },
  {
    id: 'A_5_2',
    controlId: 'A.5.2',
    controlName: 'Information security roles and responsibilities',
    question: 'Are information security roles and responsibilities clearly defined, allocated, and communicated across the organization?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'ISMS Roles & Responsibilities Matrix'
  },
  {
    id: 'A_5_3',
    controlId: 'A.5.3',
    controlName: 'Segregation of duties',
    question: 'Are conflicting duties and areas of responsibility segregated to reduce opportunities for unauthorized modification or misuse of assets?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Segregation of Duties Policy'
  },
  {
    id: 'A_5_4',
    controlId: 'A.5.4',
    controlName: 'Management responsibilities',
    question: 'Does management actively support information security through clear direction, commitment, and acknowledgment of responsibilities?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Management Commitment Statement'
  },
  {
    id: 'A_5_5',
    controlId: 'A.5.5',
    controlName: 'Contact with authorities',
    question: 'Has your organization established and maintained contacts with relevant authorities (law enforcement, regulatory bodies)?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Contact with Authorities Register'
  },
  {
    id: 'A_5_6',
    controlId: 'A.5.6',
    controlName: 'Contact with special interest groups',
    question: 'Does your organization maintain contact with special interest groups, security forums, and professional associations?',
    type: PRO_QUESTION_TYPES.TRUE_FALSE,
    policyDocument: 'External Contacts Register'
  },
  {
    id: 'A_5_7',
    controlId: 'A.5.7',
    controlName: 'Threat intelligence',
    question: 'Is threat intelligence collected, analyzed, and used to inform security decisions and risk assessments?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Threat Intelligence Policy'
  },
  {
    id: 'A_5_8',
    controlId: 'A.5.8',
    controlName: 'Information security in project management',
    question: 'Is information security integrated into project management methodologies regardless of project type?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Secure Project Management Guidelines'
  },
  {
    id: 'A_5_9',
    controlId: 'A.5.9',
    controlName: 'Inventory of information and other associated assets',
    question: 'Is there a maintained inventory of information assets and other associated assets, including owners?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Asset Management Policy + Asset Register'
  },
  {
    id: 'A_5_10',
    controlId: 'A.5.10',
    controlName: 'Acceptable use of information and other associated assets',
    question: 'Are rules for acceptable use of information and assets documented, implemented, and communicated?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Acceptable Use Policy'
  },
  {
    id: 'A_5_11',
    controlId: 'A.5.11',
    controlName: 'Return of assets',
    question: 'Do employees and external parties return all organizational assets upon termination of employment or contract?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Asset Return Procedure (HR Offboarding Checklist)'
  },
  {
    id: 'A_5_12',
    controlId: 'A.5.12',
    controlName: 'Classification of information',
    question: 'Is information classified according to confidentiality, integrity, and availability requirements?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Data Classification Policy'
  },
  {
    id: 'A_5_13',
    controlId: 'A.5.13',
    controlName: 'Labelling of information',
    question: 'Are appropriate procedures for information labelling implemented in accordance with the classification scheme?',
    type: PRO_QUESTION_TYPES.TRUE_FALSE,
    policyDocument: 'Information Labelling Procedure'
  },
  {
    id: 'A_5_14',
    controlId: 'A.5.14',
    controlName: 'Information transfer',
    question: 'Are formal transfer policies, procedures, and controls in place to protect information during transfer?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Information Transfer Policy'
  },
  {
    id: 'A_5_15',
    controlId: 'A.5.15',
    controlName: 'Access control',
    question: 'Are access control rules established and implemented based on business and security requirements?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Access Control Policy'
  },
  {
    id: 'A_5_16',
    controlId: 'A.5.16',
    controlName: 'Identity management',
    question: 'Is the full lifecycle of identities managed (creation, maintenance, disabling, removal)?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Identity Management Procedure'
  },
  {
    id: 'A_5_17',
    controlId: 'A.5.17',
    controlName: 'Authentication information',
    question: 'Is allocation and management of authentication information controlled through a formal process?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Password Management Policy'
  },
  {
    id: 'A_5_18',
    controlId: 'A.5.18',
    controlName: 'Access rights',
    question: 'Are access rights provisioned, reviewed, modified, and removed in accordance with documented procedures?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Access Rights Management Procedure'
  },
  {
    id: 'A_5_19',
    controlId: 'A.5.19',
    controlName: 'Information security in supplier relationships',
    question: 'Are information security requirements established for mitigating risks associated with suppliers?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Supplier Security Policy'
  },
  {
    id: 'A_5_20',
    controlId: 'A.5.20',
    controlName: 'Addressing information security within supplier agreements',
    question: 'Are relevant security requirements included in agreements with suppliers accessing organizational assets?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Supplier Agreement Template + NDA Template'
  },
  {
    id: 'A_5_21',
    controlId: 'A.5.21',
    controlName: 'Managing information security in the ICT supply chain',
    question: 'Are processes defined for managing information security risks in the ICT supply chain?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'ICT Supply Chain Security Policy'
  },
  {
    id: 'A_5_22',
    controlId: 'A.5.22',
    controlName: 'Monitoring, review and change management of supplier services',
    question: 'Are supplier services regularly monitored, reviewed, and changes managed securely?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Supplier Review Procedure'
  },
  {
    id: 'A_5_23',
    controlId: 'A.5.23',
    controlName: 'Information security for use of cloud services',
    question: 'Are processes for acquisition, use, management, and exit from cloud services established?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Cloud Security Policy'
  },
  {
    id: 'A_5_24',
    controlId: 'A.5.24',
    controlName: 'Information security incident management planning and preparation',
    question: 'Is incident management planned and prepared, including roles, responsibilities, and procedures?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Incident Response Plan'
  },
  {
    id: 'A_5_25',
    controlId: 'A.5.25',
    controlName: 'Assessment and decision on information security events',
    question: 'Are security events assessed and classified to determine if they qualify as incidents?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Security Event Classification Procedure'
  },
  {
    id: 'A_5_26',
    controlId: 'A.5.26',
    controlName: 'Response to information security incidents',
    question: 'Are information security incidents responded to according to documented procedures?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Incident Response Procedure'
  },
  {
    id: 'A_5_27',
    controlId: 'A.5.27',
    controlName: 'Learning from information security incidents',
    question: 'Is knowledge gained from incidents used to strengthen security controls and reduce future incidents?',
    type: PRO_QUESTION_TYPES.TRUE_FALSE,
    policyDocument: 'Incident Post-Mortem Template'
  },
  {
    id: 'A_5_28',
    controlId: 'A.5.28',
    controlName: 'Collection of evidence',
    question: 'Are procedures defined for identification, collection, acquisition, and preservation of evidence?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Evidence Collection Procedure'
  },
  {
    id: 'A_5_29',
    controlId: 'A.5.29',
    controlName: 'Information security during disruption',
    question: 'Is information security maintained at required levels during disruption?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Business Continuity Policy'
  },
  {
    id: 'A_5_30',
    controlId: 'A.5.30',
    controlName: 'ICT readiness for business continuity',
    question: 'Is ICT readiness planned, implemented, maintained, and tested to meet business continuity objectives?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Disaster Recovery Plan'
  },
  {
    id: 'A_5_31',
    controlId: 'A.5.31',
    controlName: 'Legal, statutory, regulatory and contractual requirements',
    question: 'Are all relevant legal, regulatory, and contractual requirements identified and compliance ensured?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Compliance Register + Legal Requirements Matrix'
  },
  {
    id: 'A_5_32',
    controlId: 'A.5.32',
    controlName: 'Intellectual property rights',
    question: 'Are procedures implemented to protect intellectual property rights?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Intellectual Property Policy'
  },
  {
    id: 'A_5_33',
    controlId: 'A.5.33',
    controlName: 'Protection of records',
    question: 'Are records protected from loss, destruction, falsification, and unauthorized access?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Records Management Policy'
  },
  {
    id: 'A_5_34',
    controlId: 'A.5.34',
    controlName: 'Privacy and protection of PII',
    question: 'Is personally identifiable information (PII) protected as required by applicable laws and regulations?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Data Protection & Privacy Policy'
  },
  {
    id: 'A_5_35',
    controlId: 'A.5.35',
    controlName: 'Independent review of information security',
    question: "Is the organization's approach to managing information security independently reviewed at planned intervals?",
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Internal Audit Procedure'
  },
  {
    id: 'A_5_36',
    controlId: 'A.5.36',
    controlName: 'Compliance with policies, rules and standards for information security',
    question: 'Is compliance with security policies, rules, and standards regularly reviewed?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Compliance Monitoring Procedure'
  },
  {
    id: 'A_5_37',
    controlId: 'A.5.37',
    controlName: 'Documented operating procedures',
    question: 'Are operating procedures documented and made available to personnel who need them?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Standard Operating Procedures (SOPs)'
  }
];

// A.6 - PEOPLE CONTROLS (8 Questions)
export const PEOPLE_CONTROLS = [
  {
    id: 'A_6_1',
    controlId: 'A.6.1',
    controlName: 'Screening',
    question: 'Are background verification checks on candidates carried out prior to employment and on an ongoing basis?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Pre-Employment Screening Policy'
  },
  {
    id: 'A_6_2',
    controlId: 'A.6.2',
    controlName: 'Terms and conditions of employment',
    question: "Do employment contracts state employees' and the organization's information security responsibilities?",
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Employment Contract Template + Security Clause'
  },
  {
    id: 'A_6_3',
    controlId: 'A.6.3',
    controlName: 'Information security awareness, education and training',
    question: 'Do all personnel receive appropriate security awareness, education, and training relevant to their role?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Security Awareness & Training Policy'
  },
  {
    id: 'A_6_4',
    controlId: 'A.6.4',
    controlName: 'Disciplinary process',
    question: 'Is there a formal disciplinary process for personnel who commit information security policy breaches?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Disciplinary Procedure'
  },
  {
    id: 'A_6_5',
    controlId: 'A.6.5',
    controlName: 'Responsibilities after termination or change of employment',
    question: 'Are information security responsibilities that remain valid after termination defined, enforced, and communicated?',
    type: PRO_QUESTION_TYPES.TRUE_FALSE,
    policyDocument: 'Termination/Exit Policy'
  },
  {
    id: 'A_6_6',
    controlId: 'A.6.6',
    controlName: 'Confidentiality or non-disclosure agreements',
    question: 'Are confidentiality or non-disclosure agreements identified, documented, and regularly reviewed?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Non-Disclosure Agreement (NDA) Template'
  },
  {
    id: 'A_6_7',
    controlId: 'A.6.7',
    controlName: 'Remote working',
    question: 'Are security measures implemented for remote working to protect information accessed or processed outside premises?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Remote Working Policy'
  },
  {
    id: 'A_6_8',
    controlId: 'A.6.8',
    controlName: 'Information security event reporting',
    question: 'Do personnel have a mechanism to report observed or suspected security events through appropriate channels?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Incident Reporting Procedure'
  }
];

// A.7 - PHYSICAL CONTROLS (14 Questions)
export const PHYSICAL_CONTROLS = [
  {
    id: 'A_7_1',
    controlId: 'A.7.1',
    controlName: 'Physical security perimeters',
    question: 'Are security perimeters defined and used to protect areas containing sensitive information and assets?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Physical Security Policy'
  },
  {
    id: 'A_7_2',
    controlId: 'A.7.2',
    controlName: 'Physical entry',
    question: 'Are secure areas protected by appropriate entry controls to ensure only authorized personnel access?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Physical Access Control Procedure'
  },
  {
    id: 'A_7_3',
    controlId: 'A.7.3',
    controlName: 'Securing offices, rooms and facilities',
    question: 'Is physical security designed and implemented for offices, rooms, and facilities?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Facility Security Guidelines'
  },
  {
    id: 'A_7_4',
    controlId: 'A.7.4',
    controlName: 'Physical security monitoring',
    question: 'Are premises continuously monitored for unauthorized physical access?',
    type: PRO_QUESTION_TYPES.TRUE_FALSE,
    policyDocument: 'CCTV & Monitoring Policy'
  },
  {
    id: 'A_7_5',
    controlId: 'A.7.5',
    controlName: 'Protecting against physical and environmental threats',
    question: 'Is protection against physical and environmental threats (fire, flood, earthquake) designed and implemented?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Environmental Security Procedure'
  },
  {
    id: 'A_7_6',
    controlId: 'A.7.6',
    controlName: 'Working in secure areas',
    question: 'Are security measures for working in secure areas designed and implemented?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Secure Area Working Guidelines'
  },
  {
    id: 'A_7_7',
    controlId: 'A.7.7',
    controlName: 'Clear desk and clear screen',
    question: 'Are clear desk and clear screen rules defined and appropriately enforced?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Clear Desk & Clear Screen Policy'
  },
  {
    id: 'A_7_8',
    controlId: 'A.7.8',
    controlName: 'Equipment siting and protection',
    question: 'Is equipment sited and protected to reduce environmental threats, hazards, and unauthorized access?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Equipment Protection Guidelines'
  },
  {
    id: 'A_7_9',
    controlId: 'A.7.9',
    controlName: 'Security of assets off-premises',
    question: 'Are security measures applied to off-site assets considering different risks of working outside premises?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Off-Premises Asset Security Policy'
  },
  {
    id: 'A_7_10',
    controlId: 'A.7.10',
    controlName: 'Storage media',
    question: 'Are storage media managed through their lifecycle (acquisition, use, transport, disposal) according to classification?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Removable Media Policy'
  },
  {
    id: 'A_7_11',
    controlId: 'A.7.11',
    controlName: 'Supporting utilities',
    question: 'Are information processing facilities protected from power failures and utility disruptions?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Utility & Power Management Procedure'
  },
  {
    id: 'A_7_12',
    controlId: 'A.7.12',
    controlName: 'Cabling security',
    question: 'Are power and telecommunications cables protected from interception, interference, or damage?',
    type: PRO_QUESTION_TYPES.TRUE_FALSE,
    policyDocument: 'Cabling Security Guidelines'
  },
  {
    id: 'A_7_13',
    controlId: 'A.7.13',
    controlName: 'Equipment maintenance',
    question: 'Is equipment correctly maintained to ensure availability, integrity, and confidentiality of information?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Equipment Maintenance Procedure'
  },
  {
    id: 'A_7_14',
    controlId: 'A.7.14',
    controlName: 'Secure disposal or re-use of equipment',
    question: 'Are items of equipment containing storage media verified to ensure sensitive data is removed or securely overwritten?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Secure Disposal & Sanitization Policy'
  }
];

// A.8 - TECHNOLOGICAL CONTROLS (34 Questions)
export const TECHNOLOGICAL_CONTROLS = [
  {
    id: 'A_8_1',
    controlId: 'A.8.1',
    controlName: 'User endpoint devices',
    question: 'Is information stored on, processed by, or accessible via user endpoint devices protected?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Endpoint Security Policy'
  },
  {
    id: 'A_8_2',
    controlId: 'A.8.2',
    controlName: 'Privileged access rights',
    question: 'Is allocation and use of privileged access rights restricted and managed?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Privileged Access Management Policy'
  },
  {
    id: 'A_8_3',
    controlId: 'A.8.3',
    controlName: 'Information access restriction',
    question: 'Is access to information and application functions restricted according to access control policy?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Access Control Policy'
  },
  {
    id: 'A_8_4',
    controlId: 'A.8.4',
    controlName: 'Access to source code',
    question: 'Is read and write access to source code, development tools, and software libraries appropriately managed?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Source Code Management Policy'
  },
  {
    id: 'A_8_5',
    controlId: 'A.8.5',
    controlName: 'Secure authentication',
    question: 'Are secure authentication technologies and procedures implemented based on access restrictions and classification?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Authentication Policy'
  },
  {
    id: 'A_8_6',
    controlId: 'A.8.6',
    controlName: 'Capacity management',
    question: 'Is utilization of resources monitored and adjusted, with projections made of future capacity requirements?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Capacity Management Procedure'
  },
  {
    id: 'A_8_7',
    controlId: 'A.8.7',
    controlName: 'Protection against malware',
    question: 'Is protection against malware implemented and supported by appropriate user awareness?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Anti-Malware Policy'
  },
  {
    id: 'A_8_8',
    controlId: 'A.8.8',
    controlName: 'Management of technical vulnerabilities',
    question: 'Is information about technical vulnerabilities obtained, exposure evaluated, and appropriate measures taken?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Vulnerability Management Policy'
  },
  {
    id: 'A_8_9',
    controlId: 'A.8.9',
    controlName: 'Configuration management',
    question: 'Are configurations (security, hardware, software, services, networks) established, documented, and managed?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Configuration Management Policy'
  },
  {
    id: 'A_8_10',
    controlId: 'A.8.10',
    controlName: 'Information deletion',
    question: 'Is information stored in systems, devices, or storage media deleted when no longer required?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Data Retention & Deletion Policy'
  },
  {
    id: 'A_8_11',
    controlId: 'A.8.11',
    controlName: 'Data masking',
    question: 'Is data masking used in accordance with access control policy and business requirements?',
    type: PRO_QUESTION_TYPES.TRUE_FALSE,
    policyDocument: 'Data Masking Policy'
  },
  {
    id: 'A_8_12',
    controlId: 'A.8.12',
    controlName: 'Data leakage prevention',
    question: 'Are data leakage prevention measures applied to systems, networks, and devices that process sensitive information?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Data Loss Prevention (DLP) Policy'
  },
  {
    id: 'A_8_13',
    controlId: 'A.8.13',
    controlName: 'Information backup',
    question: 'Are backup copies of information, software, and systems maintained and regularly tested according to backup policy?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Backup Policy'
  },
  {
    id: 'A_8_14',
    controlId: 'A.8.14',
    controlName: 'Redundancy of information processing facilities',
    question: 'Are information processing facilities implemented with sufficient redundancy to meet availability requirements?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'System Redundancy & High Availability Policy'
  },
  {
    id: 'A_8_15',
    controlId: 'A.8.15',
    controlName: 'Logging',
    question: 'Are logs recording activities, exceptions, faults, and security events produced, stored, protected, and analyzed?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Logging & Monitoring Policy'
  },
  {
    id: 'A_8_16',
    controlId: 'A.8.16',
    controlName: 'Monitoring activities',
    question: 'Are networks, systems, and applications monitored for anomalous behavior with appropriate response actions?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Security Monitoring Procedure'
  },
  {
    id: 'A_8_17',
    controlId: 'A.8.17',
    controlName: 'Clock synchronization',
    question: 'Are clocks of information processing systems synchronized to approved time sources?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Time Synchronization Procedure'
  },
  {
    id: 'A_8_18',
    controlId: 'A.8.18',
    controlName: 'Use of privileged utility programs',
    question: 'Is use of utility programs that can override system and application controls restricted and tightly controlled?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Privileged Utility Programs Policy'
  },
  {
    id: 'A_8_19',
    controlId: 'A.8.19',
    controlName: 'Installation of software on operational systems',
    question: 'Are procedures and measures implemented to securely manage software installation on operational systems?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Software Installation Policy'
  },
  {
    id: 'A_8_20',
    controlId: 'A.8.20',
    controlName: 'Networks security',
    question: 'Are networks and network devices secured, managed, and controlled to protect information in systems and applications?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Network Security Policy'
  },
  {
    id: 'A_8_21',
    controlId: 'A.8.21',
    controlName: 'Security of network services',
    question: 'Are security mechanisms, service levels, and requirements identified, implemented, and monitored for network services?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Network Services Security Procedure'
  },
  {
    id: 'A_8_22',
    controlId: 'A.8.22',
    controlName: 'Segregation of networks',
    question: "Are groups of information services, users, and systems segregated in the organization's networks?",
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Network Segmentation Policy'
  },
  {
    id: 'A_8_23',
    controlId: 'A.8.23',
    controlName: 'Web filtering',
    question: 'Is access to external websites managed to reduce exposure to malicious content?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Web Filtering Policy'
  },
  {
    id: 'A_8_24',
    controlId: 'A.8.24',
    controlName: 'Use of cryptography',
    question: 'Are rules for effective use of cryptography, including key management, defined and implemented?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Cryptographic Policy + Key Management Procedure'
  },
  {
    id: 'A_8_25',
    controlId: 'A.8.25',
    controlName: 'Secure development life cycle',
    question: 'Are rules for secure development of software and systems established and applied?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Secure SDLC Policy'
  },
  {
    id: 'A_8_26',
    controlId: 'A.8.26',
    controlName: 'Application security requirements',
    question: 'Are information security requirements identified, specified, and approved when developing or acquiring applications?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Application Security Requirements Document'
  },
  {
    id: 'A_8_27',
    controlId: 'A.8.27',
    controlName: 'Secure system architecture and engineering principles',
    question: 'Are principles for engineering secure systems established, documented, maintained, and applied?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Secure Architecture Guidelines'
  },
  {
    id: 'A_8_28',
    controlId: 'A.8.28',
    controlName: 'Secure coding',
    question: 'Are secure coding principles applied to software development?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Secure Coding Standards'
  },
  {
    id: 'A_8_29',
    controlId: 'A.8.29',
    controlName: 'Security testing in development and acceptance',
    question: 'Are security testing processes defined and implemented in the development life cycle?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Security Testing Procedure'
  },
  {
    id: 'A_8_30',
    controlId: 'A.8.30',
    controlName: 'Outsourced development',
    question: 'Is outsourced system development directed, monitored, and reviewed by the organization?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Outsourced Development Policy'
  },
  {
    id: 'A_8_31',
    controlId: 'A.8.31',
    controlName: 'Separation of development, test and production environments',
    question: 'Are development, testing, and production environments separated and secured?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Environment Separation Policy'
  },
  {
    id: 'A_8_32',
    controlId: 'A.8.32',
    controlName: 'Change management',
    question: 'Are changes to information processing facilities and systems subject to change management procedures?',
    type: PRO_QUESTION_TYPES.RATING,
    policyDocument: 'Change Management Policy'
  },
  {
    id: 'A_8_33',
    controlId: 'A.8.33',
    controlName: 'Test information',
    question: 'Is test information appropriately selected, protected, and managed?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Test Data Management Policy'
  },
  {
    id: 'A_8_34',
    controlId: 'A.8.34',
    controlName: 'Protection of information systems during audit testing',
    question: 'Are audit tests and activities minimizing impact on operational systems planned and agreed?',
    type: PRO_QUESTION_TYPES.YES_NO,
    policyDocument: 'Audit Testing Guidelines'
  }
];

// Combined all controls for easy access
export const ALL_PRO_CONTROLS = [
  ...ORGANIZATIONAL_CONTROLS,
  ...PEOPLE_CONTROLS,
  ...PHYSICAL_CONTROLS,
  ...TECHNOLOGICAL_CONTROLS
];

// Category metadata
export const PRO_CATEGORIES = {
  organizational: {
    id: 'organizational',
    name: 'A.5 - Organizational Controls',
    shortName: 'Organizational',
    description: 'Policies, governance, risk management, and supplier relationships',
    controlRange: 'A.5.1 - A.5.37',
    count: 37,
    icon: 'Building2',
    color: 'blue',
    controls: ORGANIZATIONAL_CONTROLS
  },
  people: {
    id: 'people',
    name: 'A.6 - People Controls',
    shortName: 'People',
    description: 'Human resources security, awareness, and training',
    controlRange: 'A.6.1 - A.6.8',
    count: 8,
    icon: 'Users',
    color: 'purple',
    controls: PEOPLE_CONTROLS
  },
  physical: {
    id: 'physical',
    name: 'A.7 - Physical Controls',
    shortName: 'Physical',
    description: 'Physical security, environmental protection, and equipment',
    controlRange: 'A.7.1 - A.7.14',
    count: 14,
    icon: 'Shield',
    color: 'green',
    controls: PHYSICAL_CONTROLS
  },
  technological: {
    id: 'technological',
    name: 'A.8 - Technological Controls',
    shortName: 'Technological',
    description: 'Technical security measures, network, and development security',
    controlRange: 'A.8.1 - A.8.34',
    count: 34,
    icon: 'Cpu',
    color: 'orange',
    controls: TECHNOLOGICAL_CONTROLS
  }
};

// Helper function to calculate score for a single answer
export const calculateProScore = (questionType, answer) => {
  if (!answer) return 0;
  
  const scoring = PRO_SCORING[questionType];
  if (!scoring) return 0;
  
  // For rating type, answer is a number 1-5
  if (questionType === 'rating') {
    return parseInt(answer) || 0;
  }
  
  // For yes_no and true_false, lookup the score
  return scoring[answer.toLowerCase()] || 0;
};

// Helper function to calculate category score
export const calculateCategoryScore = (controls, answers) => {
  let totalScore = 0;
  let maxPossible = 0;
  let answeredCount = 0;
  
  controls.forEach(control => {
    const answer = answers[control.id];
    if (answer !== undefined && answer !== null && answer !== '') {
      totalScore += calculateProScore(control.type, answer);
      answeredCount++;
    }
    maxPossible += PRO_SCORING[control.type]?.maxScore || 5;
  });
  
  return {
    score: totalScore,
    maxPossible,
    percentage: maxPossible > 0 ? Math.round((totalScore / maxPossible) * 100) : 0,
    answeredCount,
    totalQuestions: controls.length,
    completionRate: Math.round((answeredCount / controls.length) * 100)
  };
};

// Helper function to calculate overall Pro assessment score
export const calculateProOverallScore = (answers) => {
  const categoryScores = {
    organizational: calculateCategoryScore(ORGANIZATIONAL_CONTROLS, answers.organizational || {}),
    people: calculateCategoryScore(PEOPLE_CONTROLS, answers.people || {}),
    physical: calculateCategoryScore(PHYSICAL_CONTROLS, answers.physical || {}),
    technological: calculateCategoryScore(TECHNOLOGICAL_CONTROLS, answers.technological || {})
  };
  
  // Calculate weighted overall score
  const weightedScore = 
    (categoryScores.organizational.percentage * PRO_CATEGORY_WEIGHTS.organizational) +
    (categoryScores.people.percentage * PRO_CATEGORY_WEIGHTS.people) +
    (categoryScores.physical.percentage * PRO_CATEGORY_WEIGHTS.physical) +
    (categoryScores.technological.percentage * PRO_CATEGORY_WEIGHTS.technological);
  
  // Calculate total completion
  const totalAnswered = 
    categoryScores.organizational.answeredCount +
    categoryScores.people.answeredCount +
    categoryScores.physical.answeredCount +
    categoryScores.technological.answeredCount;
  
  const totalQuestions = 93;
  
  return {
    categoryScores,
    overallPercentage: Math.round(weightedScore),
    maturityLevel: getProMaturityLevel(Math.round(weightedScore)),
    completionRate: Math.round((totalAnswered / totalQuestions) * 100),
    totalAnswered,
    totalQuestions
  };
};

// Get maturity level based on percentage
export const getProMaturityLevel = (percentage) => {
  if (percentage >= 90) return { level: 5, name: 'Optimizing', description: 'Continuous improvement, industry-leading practices' };
  if (percentage >= 75) return { level: 4, name: 'Managed', description: 'Quantitatively controlled and measured' };
  if (percentage >= 55) return { level: 3, name: 'Defined', description: 'Standardized processes across organization' };
  if (percentage >= 35) return { level: 2, name: 'Developing', description: 'Basic processes in place, inconsistent application' };
  return { level: 1, name: 'Initial', description: 'Ad-hoc processes, reactive approach' };
};

export default {
  PRO_QUESTION_TYPES,
  PRO_SCORING,
  PRO_CATEGORY_WEIGHTS,
  ORGANIZATIONAL_CONTROLS,
  PEOPLE_CONTROLS,
  PHYSICAL_CONTROLS,
  TECHNOLOGICAL_CONTROLS,
  ALL_PRO_CONTROLS,
  PRO_CATEGORIES,
  calculateProScore,
  calculateCategoryScore,
  calculateProOverallScore,
  getProMaturityLevel
};
